import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { Coins, HandCoins, ChevronRight, Calculator, RefreshCcw, TrendingUp, Clock, Copy, X, Trash2, CheckCircle2, Banknote, Landmark, Check, CalendarClock, Download, Calendar, ArrowRight, Save, Receipt, RefreshCw, FileText, DownloadCloud, PenLine, Pencil, Search, Filter, Plus, Bot, User, ShieldCheck, CreditCard, ShoppingBag, Edit2, ClipboardList, Wallet, PieChart, Shield, Zap, Mail } from 'lucide-react';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { exportToPDF } from '@/utils/exportPDF';
import { sendSalarySlipEmail } from '@/utils/salarySlipEmail';

const ROLE_LABEL_MAP = {
  owner: "Owner",
  manager: "Manager",
  staff: "Staf",
  intern: "Magang"
};

const ALLOWANCE_OPTIONS = [
  "Tunjangan Jabatan & Keahlian",
  "Tunjangan Transportasi & Transport",
  "Tunjangan Kinerja & Kehadiran",
  "Bonus Tahunan / THR"
];

const DEDUCTION_OPTIONS = [
  "BPJS Ketenagakerjaan (2%)",
  "BPJS Kesehatan (1%)",
  "PPh Pasal 21 (Pajak Penghasilan)",
  "Potongan Kasbon / Pinjaman",
  "Potongan Keterlambatan"
];

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const calculateTHP = (emp) => {
  const base = Number(emp.base_salary || 0);
  const allow = (emp.fixed_allowances || []).reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const deduc = (emp.fixed_deductions || []).reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const extraAllow = (emp.extra_components || []).filter(c => c.type === 'allowance').reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const extraDeduc = (emp.extra_components || []).filter(c => c.type === 'deduction').reduce((sum, c) => sum + Number(c.amount || 0), 0);
  return base + allow - deduc + extraAllow - extraDeduc;
};

const terbilang = (angka) => {
  if (angka === 0) return "Nol";
  const b = ["", "Satu", "Dua", "Tiga", "Empat", "Lima", "Enam", "Tujuh", "Delapan", "Sembilan", "Sepuluh", "Sebelas"];
  if (angka < 12) return b[angka];
  if (angka < 20) return terbilang(angka - 10) + " Belas";
  if (angka < 100) return terbilang(Math.floor(angka / 10)) + " Puluh " + (angka % 10 > 0 ? terbilang(angka % 10) : "");
  if (angka < 200) return "Seratus " + (angka - 100 > 0 ? terbilang(angka - 100) : "");
  if (angka < 1000) return terbilang(Math.floor(angka / 100)) + " Ratus " + (angka % 100 > 0 ? terbilang(angka % 100) : "");
  if (angka < 2000) return "Seribu " + (angka - 1000 > 0 ? terbilang(angka - 1000) : "");
  if (angka < 1000000) return terbilang(Math.floor(angka / 1000)) + " Ribu " + (angka % 1000 > 0 ? terbilang(angka % 1000) : "");
  if (angka < 1000000000) return terbilang(Math.floor(angka / 1000000)) + " Juta " + (angka % 1000000 > 0 ? terbilang(angka % 1000000) : "");
  if (angka < 1000000000000) return terbilang(Math.floor(angka / 1000000000)) + " Milyar " + (angka % 1000000000 > 0 ? terbilang(angka % 1000000000) : "");
  return "";
};

const calculateTotalAllowances = (emp) => (emp.fixed_allowances || []).reduce((sum, c) => sum + Number(c.amount || 0), 0);
const calculateTotalDeductions = (emp) => (emp.fixed_deductions || []).reduce((sum, c) => sum + Number(c.amount || 0), 0);

export default function FinancePayroll() {
  const queryClient = useQueryClient();
  const { accountsMap: accounts = {} } = useBankAccounts();

  const [search, setSearch] = useState('');
  const [showAsa, setShowAsa] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // Payroll Cart States
  const [cart, setCart] = useState([]); // array of employee objects
  const [selectedForPopup, setSelectedForPopup] = useState(null); // the employee currently being viewed in the Transfer Popup
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [payForm, setPayForm] = useState({ bankAccountId: 'bca', splitMode: 'split', coa_allocations: [] });

  // Batch Selection State
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [editItem, setEditItem] = useState(null);

  // Add Employee Form State
  const [empForm, setEmpForm] = useState({
    full_name: '',
    email: '',
    bank_name: 'BCA',
    bank_account: '',
    account_name: '',
    base_salary: '',
    company_category: 'SDE',
    reset_schedule: 'bulanan',
    monthly_timing: 'akhir_bulan',
    salary_components: [],
  });

  const [companyCategoryFilter, setCompanyCategoryFilter] = useState('ALL');
  const [sourceAccount, setSourceAccount] = useState('Satala Dermatech Essential (SDE)');
  const [isEditingPeriod, setIsEditingPeriod] = useState(false);
  const [paymentPeriod, setPaymentPeriod] = useState(() => {
    const d = new Date();
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  });

  // Fetch Payroll Employees
  const { data: employees = [], isLoading, error } = useQuery({
    queryKey: ['employee-payroll'],
    queryFn: () => base44.entities.EmployeePayroll.list()
  });

  // Fetch Team Members for Role & Email crosscheck
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => base44.entities.TeamMember.list()
  });

  // Options for auto-filling employee info from database (hanya anggota tim/karyawan)
  const employeeNameOptions = useMemo(() => {
    const list = [];
    teamMembers.forEach(m => {
      if (m.full_name) {
        list.push({
          value: m.full_name,
          label: `👤 ${m.full_name} (${m.role || m.position || 'Tim'})`,
          data: {
            full_name: m.full_name,
            email: m.email || '',
            account_name: (m.full_name || '').toUpperCase(),
            bank_name: m.bank_name || 'BCA',
            bank_account: m.bank_account || ''
          }
        });
      }
    });

    return list;
  }, [teamMembers]);

  // Fetch Chart of Accounts for payment
  const { data: coaList = [] } = useQuery({
    queryKey: ['chart-of-accounts'],
    queryFn: () => base44.entities.ChartOfAccounts.list(),
  });

  const { data: companySettingsList = [] } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list()
  });
  const companySettings = companySettingsList[0] || {};

  // Mutations
  const updateEmpMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.EmployeePayroll.update(id, data)
  });

  const createEmpMut = useMutation({
    mutationFn: (data) => base44.entities.EmployeePayroll.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-payroll'] });
      setShowAddModal(false);
      toast.success('Karyawan berhasil ditambahkan ke database Gaji.');
    }
  });

  const deleteEmpMut = useMutation({
    mutationFn: (id) => base44.entities.EmployeePayroll.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-payroll'] });
      toast.success('Data karyawan berhasil dihapus.');
    },
    onError: (err) => {
      toast.error('Gagal menghapus data karyawan: ' + (err.message || 'Terjadi kesalahan'));
    }
  });

  const handleDeleteSingle = (emp) => {
    if (!emp) return;
    if (window.confirm(`Apakah Anda yakin ingin menghapus data karyawan "${emp.full_name}" dari database gaji?`)) {
      deleteEmpMut.mutate(emp.id, {
        onSuccess: () => {
          if (showAddModal) setShowAddModal(false);
          setCart(prev => prev.filter(c => c.id !== emp.id));
        }
      });
    }
  };

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.AccountMutation.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-mutations'] })
  });

  // Process data for display and trigger lazy updates
  const processedEmployees = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();
    const lastDayCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    return employees.map(emp => {
      let currentStatus = emp.status || 'Belum Gajian';
      let needsDbUpdate = false;

      if (currentStatus === 'Sudah Gajian') {
        const schedule = emp.reset_schedule || 'bulanan';
        const timing = emp.monthly_timing || 'akhir_bulan';
        
        let shouldReset = false;

        // 1. Cek berdasarkan next_reset_date
        if (emp.next_reset_date) {
          const resetDate = new Date(emp.next_reset_date);
          if (now >= resetDate) {
            shouldReset = true;
          }
        }

        // 2. Cek untuk siklus bulanan Akhir Bulan (Tgl 28-31)
        if (schedule === 'bulanan' && timing === 'akhir_bulan') {
          // Jika hari ini sudah akhir bulan (tgl >= 28)
          if (currentDate >= 28 || currentDate >= lastDayCurrentMonth) {
            const lastPaid = emp.last_paid_date ? new Date(emp.last_paid_date) : null;
            // Jika belum dibayar pada periode akhir bulan ini (28-31 bulan ini)
            const paidThisPeriod = lastPaid && 
              lastPaid.getFullYear() === currentYear && 
              lastPaid.getMonth() === currentMonth && 
              lastPaid.getDate() >= 28;

            if (!paidThisPeriod) {
              shouldReset = true;
            }
          }
        }

        if (shouldReset) {
          currentStatus = 'Belum Gajian';
          needsDbUpdate = true;
        }
      }

      if (needsDbUpdate) {
        // Fire and forget lazy update
        updateEmpMut.mutate({ id: emp.id, data: { status: 'Belum Gajian' } });
      }

      const teamMember = teamMembers.find(m => m.full_name?.toLowerCase() === emp.full_name?.toLowerCase());
      const resolvedEmail = emp.email || teamMember?.email || '';

      return {
        ...emp,
        email: resolvedEmail,
        computedStatus: currentStatus,
        computedTHP: calculateTHP(emp),
        totalAllowances: calculateTotalAllowances(emp),
        totalDeductions: calculateTotalDeductions(emp),
      };
    });
  }, [employees, teamMembers]);

  // Filter Search & Company Category
  const filteredEmployees = useMemo(() => {
    let result = processedEmployees;
    if (companyCategoryFilter !== 'ALL') {
      result = result.filter(e => (e.company_category || 'SDE') === companyCategoryFilter);
    }
    if (search) {
      result = result.filter(e => e.full_name.toLowerCase().includes(search.toLowerCase()));
    }
    return result;
  }, [processedEmployees, search, companyCategoryFilter]);

  // ASA Calculations
  const asaStats = useMemo(() => {
    let countBelum = 0;
    let totalBelum = 0;
    processedEmployees.forEach(e => {
      if (e.computedStatus === 'Belum Gajian') {
        countBelum++;
        totalBelum += Number(e.computedTHP || 0);
      }
    });
    return { countBelum, totalBelum };
  }, [processedEmployees]);

  // Cart Calculations
  const cartTotalAmount = useMemo(() => {
    return cart.reduce((sum, emp) => sum + Number(calculateTHP(emp) || 0), 0);
  }, [cart]);

  // Actions
  const handleOpenAdd = () => {
    setEditItem(null);
    setEmpForm({
      full_name: '',
      email: '',
      bank_name: 'BCA',
      bank_account: '',
      account_name: '',
      base_salary: '',
      company_category: 'SDE',
      reset_schedule: 'bulanan',
      monthly_timing: 'akhir_bulan',
      salary_components: [],
    });
    setShowAddModal(true);
  };

  const handleOpenEdit = (emp) => {
    setEditItem(emp);

    const allowances = (emp.fixed_allowances || []).map(a => ({ ...a, type: 'allowance' }));
    const deductions = (emp.fixed_deductions || []).map(d => ({ ...d, type: 'deduction' }));

    setEmpForm({
      full_name: emp.full_name || '',
      email: emp.email || '',
      bank_name: emp.bank_name || 'BCA',
      bank_account: emp.bank_account || '',
      account_name: emp.account_name || '',
      base_salary: emp.base_salary || '',
      company_category: emp.company_category || 'SDE',
      reset_schedule: emp.reset_schedule || 'bulanan',
      monthly_timing: emp.monthly_timing || 'akhir_bulan',
      salary_components: [...allowances, ...deductions],
    });
    setShowAddModal(true);
  };

  const toggleBatchMode = () => {
    setIsBatchMode(!isBatchMode);
    setSelectedForDelete([]);
  };

  const handlePayAllDirectly = () => {
    const unpaid = filteredEmployees.filter(e => e.computedStatus === 'Belum Gajian');
    if (unpaid.length === 0) {
      toast.info('Semua karyawan sudah berstatus Sudah Gajian!');
      return;
    }
    setCart(unpaid);
    setShowFinalizeModal(true);
  };

  const handleDeleteBatch = async () => {
    if (selectedForDelete.length === 0) {
      toast.error('Pilih minimal 1 karyawan untuk dihapus.');
      return;
    }
    if (!confirm(`Yakin ingin menghapus ${selectedForDelete.length} data karyawan?`)) return;

    // Process deletion
    try {
      for (const id of selectedForDelete) {
        await base44.entities.EmployeePayroll.delete(id);
      }
      queryClient.invalidateQueries({ queryKey: ['employee-payroll'] });
      toast.success(`${selectedForDelete.length} data karyawan berhasil dihapus.`);
      setShowFinalizeModal(false);
      setCart([]);
      setIsBatchMode(false);
      setSelectedForDelete([]);
    } catch (err) {
      toast.error('Terjadi kesalahan saat memproses pembayaran.');
    }
  };

  const generateSlipPDF = async () => {
    if (!selectedForPopup) return;

    // Buka tab baru DULUAN supaya tidak diblokir browser karena proses async
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write('<div style="font-family: sans-serif; padding: 20px;">Memuat dokumen Slip Gaji...</div>');
    }

    // Crosscheck with team members
    const empData = selectedForPopup;
    const teamMember = teamMembers.find(m => m.full_name?.toLowerCase() === empData.full_name?.toLowerCase());
    const role = teamMember?.role || 'Karyawan';
    const email = teamMember?.email || '-';

    // Find Finance Manager
    const financeManager = teamMembers.find(m => m.role?.toLowerCase().includes('finance'))?.full_name || 'Finance Manager';

    try {
      const element = document.getElementById('pdf-salary-slip-template');
      if (!element) throw new Error("Template not found");

      const canvas = await html2canvas(element, { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');

      const pdfWidth = 210; // Standard A4 width in mm
      const imgRatio = canvas.width / canvas.height;
      const pdfHeight = pdfWidth / imgRatio;

      const doc = new jsPDF({ format: 'a4', unit: 'mm' });
      doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      const blobUrl = doc.output('bloburl');
      if (printWindow) {
        printWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank');
      }
    } catch (e) {
      console.error("Failed to generate PDF", e);
      if (printWindow) printWindow.close();
      toast.error("Gagal membuat dokumen PDF");
    }
  };

  const generateSlipPDFBase64 = async (empData) => {
    setSelectedForPopup(empData);
    await new Promise(r => setTimeout(r, 400));
    const element = document.getElementById('pdf-salary-slip-template');
    if (!element) {
      console.warn("Template element not found in DOM");
      return null;
    }
    try {
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        allowTaint: true,
        backgroundColor: '#faf8f5',
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = 210; // Standard A4 width in mm
      const imgRatio = canvas.width / canvas.height;
      const pdfHeight = pdfWidth / imgRatio;
      const doc = new jsPDF({ format: 'a4', unit: 'mm' });
      doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      const arrayBuffer = doc.output('arraybuffer');
      let binary = '';
      const bytes = new Uint8Array(arrayBuffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
    } catch (err) {
      console.warn("Failed generating PDF from DOM template:", err);
      return null;
    }
  };

  const handleSubmitEmployee = (e) => {
    e.preventDefault();

    // Convert salary_components back to fixed_allowances and fixed_deductions
    const dataToSave = { ...empForm, base_salary: Number(empForm.base_salary) };
    dataToSave.fixed_allowances = (dataToSave.salary_components || []).filter(c => c.type === 'allowance').map(({ type, ...rest }) => rest);
    dataToSave.fixed_deductions = (dataToSave.salary_components || []).filter(c => c.type === 'deduction').map(({ type, ...rest }) => rest);
    delete dataToSave.salary_components;

    if (editItem) {
      updateEmpMut.mutate({
        id: editItem.id,
        data: dataToSave
      }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['employee-payroll'] });
          setShowAddModal(false);
          toast.success('Data karyawan berhasil diperbarui.');
        }
      });
    } else {
      createEmpMut.mutate({
        ...dataToSave,
        status: 'Sudah Gajian',
        last_paid_date: new Date().toISOString(),
        next_reset_date: getNextResetDate(empForm.reset_schedule, empForm.monthly_timing)
      });
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} berhasil disalin!`);
  };

  const addToCart = () => {
    if (selectedForPopup) {
      setCart(prev => [...prev, selectedForPopup]);
      toast.success(`${selectedForPopup.full_name} ditambahkan ke antrean.`);
      setSelectedForPopup(null);
    }
  };

  const handleDirectPay = async () => {
    if (!selectedForPopup) return;
    const emp = selectedForPopup;
    const nowISO = new Date().toISOString();
    const nextDate = getNextResetDate(emp.reset_schedule || 'bulanan', emp.monthly_timing || 'akhir_bulan');
    const thpAmount = calculateTHP(emp);

    try {
      // 1. Direct update employee status
      await updateEmpMut.mutateAsync({
        id: emp.id,
        data: {
          status: 'Sudah Gajian',
          last_paid_date: nowISO,
          next_reset_date: nextDate
        }
      });

      // 2. Create Mutation Record
      await createMutation.mutateAsync({
        date: nowISO.split('T')[0],
        type: 'keluar',
        account: sourceAccount,
        amount: Number(thpAmount),
        description: `Pembayaran Gaji - ${emp.full_name} (${paymentPeriod})`,
        reference_id: `payroll-${emp.id}-${Date.now()}`
      });

      // 3. Create Operational Expense Record (Physical record, no auto-sync)
      await base44.entities.OperationalExpense.create({
        date: nowISO.split('T')[0],
        category: 'Gaji & Bonus',
        requester: emp.department || 'Office',
        amount: Number(thpAmount),
        bank_account_id: sourceAccount,
        description: `Pembayaran Gaji - ${emp.full_name} (${paymentPeriod})`
      });

      queryClient.invalidateQueries({ queryKey: ['employee-payroll'] });
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      queryClient.invalidateQueries({ queryKey: ['operational-expenses'] });
      // 4. Kirim Slip Gaji ke Email Karyawan secara otomatis
      const empEmail = emp.email || teamMembers.find(m => m.full_name?.toLowerCase() === emp.full_name?.toLowerCase())?.email;
      if (empEmail) {
        toast.loading(`Mengirim slip gaji ke email ${empEmail}...`, { id: 'send-slip-email' });
        try {
          const pdfBase64 = await generateSlipPDFBase64(emp);
          const emailRes = await sendSalarySlipEmail({
            employee: { ...emp, email: empEmail },
            paymentPeriod,
            companySettings,
            sourceAccount,
            pdfBase64
          });
          if (emailRes.success) {
            toast.success(`Slip gaji otomatis terkirim ke email (${empEmail})`, { id: 'send-slip-email' });
          } else if (emailRes.isDomainRestriction) {
            toast.warning(emailRes.message, { id: 'send-slip-email', duration: 8000 });
          } else {
            toast.error(emailRes.message, { id: 'send-slip-email' });
          }
        } catch (mailErr) {
          toast.error('Gagal mengirim email: ' + mailErr.message, { id: 'send-slip-email' });
        } finally {
          setSelectedForPopup(null);
        }
      } else {
        setSelectedForPopup(null);
        toast.info(`Catatan: Email untuk ${emp.full_name} belum tercantum.`, { duration: 4000 });
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memproses pembayaran gaji.');
    }
  };

  const removeFromCart = (empId) => {
    setCart(prev => prev.filter(e => e.id !== empId));
  };

  const getNextResetDate = (schedule, monthlyTiming = 'akhir_bulan') => {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    const currentDate = d.getDate();

    if (schedule === 'harian') {
      const nextDay = new Date(d);
      nextDay.setDate(d.getDate() + 1);
      nextDay.setHours(0, 0, 0, 0);
      return nextDay.toISOString();
    } else if (schedule === 'mingguan') {
      // Set to next Monday (0 = Sunday, 1 = Monday)
      const nextMonday = new Date(d);
      const day = d.getDay();
      const diff = (day === 0 ? 1 : 8 - day);
      nextMonday.setDate(d.getDate() + diff);
      nextMonday.setHours(0, 0, 0, 0);
      return nextMonday.toISOString();
    } else {
      // Bulanan
      if (monthlyTiming === 'awal_bulan') {
        // Reset tepat pada tanggal 1 bulan berikutnya pukul 00:00
        const nextMonthFirstDay = new Date(year, month + 1, 1, 0, 0, 0);
        return nextMonthFirstDay.toISOString();
      } else {
        // Akhir Bulan: Karena baru saja dibayar untuk gaji bulan ini, reset berikutnya adalah pada hari TERAKHIR bulan DEPAN (misal 31 Okt, 30 Nov)
        const lastDayNextMonth = new Date(year, month + 2, 0).getDate();
        const targetResetDate = new Date(year, month + 1, lastDayNextMonth, 0, 0, 0);
        return targetResetDate.toISOString();
      }
    }
  };

  const handleFinalizeBulkPay = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;

    const accountName = accounts[payForm.bankAccountId]?.name || 'Mandiri Satala';
    const nowISO = new Date().toISOString();

    const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    const currentMonthName = monthNames[new Date().getMonth()];
    const isSplit = payForm.splitMode === 'split';

    // 1. Bulk Update Employee Statuses
    for (const emp of cart) {
      const nextDate = getNextResetDate(emp.reset_schedule, emp.monthly_timing || 'akhir_bulan');
      await updateEmpMut.mutateAsync({
        id: emp.id,
        data: {
          status: 'Sudah Gajian',
          last_paid_date: nowISO,
          next_reset_date: nextDate
        }
      });
    }

    if (isSplit) {
      // 2a. Create Individual Mutation & Operational Expense per Employee (Pecah 1 per 1)
      for (const emp of cart) {
        const empTHP = calculateTHP(emp);
        const empDesc = `Pembayaran Gaji - ${emp.full_name} (Bulan ${currentMonthName})`;

        // Mutation per employee
        await createMutation.mutateAsync({
          date: nowISO.split('T')[0],
          type: 'keluar',
          account: accountName,
          amount: Number(empTHP),
          description: empDesc,
          reference_id: `payroll-${emp.id}-${Date.now()}`
        });

        // Operational Expense per employee
        await base44.entities.OperationalExpense.create({
          date: nowISO.split('T')[0],
          category: 'Gaji & Bonus',
          requester: emp.department || 'Office',
          amount: Number(empTHP),
          bank_account_id: payForm.bankAccountId,
          description: empDesc
        });
      }
      toast.success(`Berhasil mencatat ${cart.length} pembayaran gaji individual dari ${accountName}.`);
    } else {
      // 2b. Create Consolidated Bulk Mutation & Operational Expense (Gabung 1 transaksi)
      const bulkDesc = `Pembayaran Gaji Karyawan Bulan ${currentMonthName} - Total ${cart.length} Orang`;
      
      await createMutation.mutateAsync({
        date: nowISO.split('T')[0],
        type: 'keluar',
        account: accountName,
        amount: cartTotalAmount,
        description: bulkDesc,
        reference_id: `bulk-payroll-${Date.now()}`,
        coa_allocations: payForm.coa_allocations
      });

      await base44.entities.OperationalExpense.create({
        date: nowISO.split('T')[0],
        category: 'Gaji & Bonus',
        requester: 'Office',
        amount: cartTotalAmount,
        bank_account_id: payForm.bankAccountId,
        description: bulkDesc
      });

      toast.success(`Berhasil mencatat pembayaran gaji total Rp ${fmt(cartTotalAmount)} dari ${accountName}.`);
    }

    queryClient.invalidateQueries({ queryKey: ['employee-payroll'] });
    queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
    queryClient.invalidateQueries({ queryKey: ['operational-expenses'] });

    // 3. Kirim Slip Gaji Otomatis ke Email masing-masing karyawan
    let sentCount = 0;
    let failedCount = 0;
    for (const emp of cart) {
      const empEmail = emp.email || teamMembers.find(m => m.full_name?.toLowerCase() === emp.full_name?.toLowerCase())?.email;
      if (empEmail) {
        try {
          const pdfBase64 = await generateSlipPDFBase64(emp);
          const res = await sendSalarySlipEmail({
            employee: { ...emp, email: empEmail },
            paymentPeriod: `Bulan ${currentMonthName}`,
            companySettings,
            sourceAccount: accountName,
            pdfBase64
          });
          if (res.success) sentCount++;
          else failedCount++;
        } catch {
          failedCount++;
        }
      }
    }
    if (sentCount > 0) {
      toast.success(`Slip gaji otomatis terkirim ke email ${sentCount} karyawan.`);
    }

    // Reset states
    setCart([]);
    setShowFinalizeModal(false);
  };

  const handleExportPDF = () => {
    exportToPDF({
      title: 'Database Gaji Karyawan',
      subtitle: `Total Karyawan: ${employees.length} | Belum Gajian: ${asaStats.countBelum}`,
      filename: `Database-Gaji`,
      columns: [
        { key: 'full_name', label: 'Nama Karyawan', render: r => r.full_name },
        { key: 'bank', label: 'Rekening', render: r => `${r.bank_name} - ${r.bank_account}` },
        { key: 'base_salary', label: 'Gaji Pokok', render: r => `Rp ${fmt(r.base_salary)}` },
        { key: 'schedule', label: 'Siklus', render: r => r.reset_schedule?.toUpperCase() },
        { key: 'status', label: 'Status Saat Ini', render: r => r.computedStatus }
      ],
      rows: employees
    });
  };

  const handleSelectAllUnpaid = () => {
    const unpaid = employees.filter(e => e.computedStatus === 'Belum Gajian');
    if (unpaid.length === 0) return;

    const newCart = [...cart];
    let addedCount = 0;
    unpaid.forEach(emp => {
      if (!newCart.some(c => c.id === emp.id)) {
        newCart.push(emp);
        addedCount++;
      }
    });

    if (addedCount > 0) {
      setCart(newCart);
      toast.success(`${addedCount} karyawan dimasukkan ke antrean.`);
    }
  };

  return (
    <div className="space-y-6 pb-24 text-[#302621]">

      {/* ─── STICKY CART BAR ─── */}
      {cart.length > 0 && (
        <div className="sticky top-0 z-30 bg-white border-b border-blue-200 shadow-md py-4 px-6 mx-[-1.5rem] sm:mx-0 sm:rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 animate-in slide-in-from-top-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0 shadow-inner">
              <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <p className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest">Antrean Pembayaran Gaji</p>
              <h3 className="text-lg md:text-xl font-black text-blue-900 mt-0.5">{cart.length} Karyawan Terpilih</h3>
            </div>
          </div>
          <div className="flex items-center gap-6 w-full sm:w-auto">
            <div className="text-right flex-1 sm:flex-none">
              <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Tagihan (1 Mutasi)</p>
              <p className="text-xl md:text-2xl font-black text-[#8C6208]">Rp {fmt(cartTotalAmount)}</p>
            </div>
            <Button
              onClick={() => setShowFinalizeModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5" /> Selesaikan Mutasi
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-2">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">Database Gaji</h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            Sistem pembayaran gaji otomatis berdasarkan siklus (Harian/Mingguan/Bulanan).
          </p>
        </div>
        <div className="flex gap-2">
          {isBatchMode ? (
            <>
              <Button onClick={toggleBatchMode} variant="outline" className="text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl h-10">
                Batal
              </Button>
              <Button onClick={handleDeleteBatch} className="bg-rose-600 text-white hover:bg-rose-700 gap-2 text-xs font-bold rounded-xl h-10 shadow-sm" disabled={selectedForDelete.length === 0}>
                <Trash2 className="w-4 h-4" /> Hapus Terpilih ({selectedForDelete.length})
              </Button>
            </>
          ) : (
            <>
              {asaStats.countBelum > 0 && (
                <Button
                  onClick={handlePayAllDirectly}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs h-10 px-5 rounded-xl shadow-md flex items-center gap-2 transition-all"
                >
                  <Zap className="w-4 h-4 fill-white text-white" /> Gaji Sekaligus Semua ({asaStats.countBelum} Karyawan)
                </Button>
              )}
              <Button onClick={handleExportPDF} variant="outline" className="text-xs font-bold border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl h-10 hidden sm:flex">
                Ekspor PDF
              </Button>
              <Button onClick={toggleBatchMode} variant="outline" className="text-xs font-bold border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl h-10 flex items-center gap-1.5">
                <Trash2 className="w-4 h-4 text-rose-500" /> Mode Hapus
              </Button>
              <Button onClick={handleOpenAdd} className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm">
                <Plus className="w-4 h-4" /> Tambah Karyawan
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ASA Widget */}
      {showAsa && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-4 md:p-5 shadow-sm relative">
          <button onClick={() => setShowAsa(false)} className="absolute top-4 right-4 text-blue-300 hover:text-blue-600">
            <X className="w-5 h-5" />
          </button>
          <div className="flex gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-sm flex items-center justify-center text-blue-600 shrink-0">
              <Bot className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <h4 className="font-black text-blue-900 flex items-center gap-2 text-sm md:text-base">
                Analisa Penggajian ASA
                <span className="text-[9px] bg-blue-600 text-white px-2 py-0.5 rounded-full uppercase tracking-widest">AI Analyst</span>
              </h4>
              <p className="text-xs text-blue-800 mt-2 max-w-2xl leading-relaxed">
                Saat ini terdapat <strong className="text-blue-900">{asaStats.countBelum} karyawan</strong> yang berstatus <strong className="text-rose-600">Belum Gajian</strong> pada siklusnya. Total dana yang perlu disiapkan untuk membayar mereka semua adalah <strong className="text-blue-900 text-sm bg-white px-2 py-0.5 rounded-md border border-blue-200">Rp {fmt(asaStats.totalBelum)}</strong>.
              </p>
              {asaStats.countBelum > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    onClick={handlePayAllDirectly}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs h-9 px-4 rounded-lg shadow-sm flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4 fill-white text-white" /> Gaji Sekaligus Semua ({asaStats.countBelum} Karyawan)
                  </Button>
                  <Button
                    onClick={handleSelectAllUnpaid}
                    variant="outline"
                    className="border-blue-300 text-blue-700 bg-white hover:bg-blue-50 font-bold text-xs h-9 rounded-lg shadow-2xs flex items-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4" /> Masukkan ke Antrean Dulu
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search & Company Filter */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cari nama karyawan..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-10 text-xs rounded-xl border-[#E6D5B8]"
          />
        </div>

        {/* Company Filter Tabs */}
        <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 gap-1 shrink-0">
          <button
            onClick={() => setCompanyCategoryFilter('ALL')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              companyCategoryFilter === 'ALL'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Semua
          </button>
          <button
            onClick={() => setCompanyCategoryFilter('SDE')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              companyCategoryFilter === 'SDE'
                ? 'bg-[#8C6208] text-white shadow-sm'
                : 'text-slate-600 hover:text-[#8C6208]'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            SDE
          </button>
          <button
            onClick={() => setCompanyCategoryFilter('ASB')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              companyCategoryFilter === 'ASB'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-blue-600'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-blue-400"></span>
            ASB
          </button>
        </div>

        {/* Quick Select All Button */}
        {filteredEmployees.some(e => e.computedStatus === 'Belum Gajian') && (
          <Button
            type="button"
            onClick={() => {
              const unpaid = filteredEmployees.filter(e => e.computedStatus === 'Belum Gajian');
              const allSelected = unpaid.every(u => cart.some(c => c.id === u.id));
              if (allSelected) {
                setCart(prev => prev.filter(c => !unpaid.some(u => u.id === c.id)));
              } else {
                setCart(prev => {
                  const existingIds = new Set(prev.map(c => c.id));
                  const newUnpaid = unpaid.filter(u => !existingIds.has(u.id));
                  return [...prev, ...newUnpaid];
                });
              }
            }}
            variant="outline"
            className="h-10 text-xs font-bold border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl shrink-0 flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-4 h-4 text-blue-600" />
            {filteredEmployees.filter(e => e.computedStatus === 'Belum Gajian').every(u => cart.some(c => c.id === u.id))
              ? 'Batal Pilih Semua'
              : 'Pilih Semua'}
          </Button>
        )}
      </div>

      {/* Cards Grid */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#8C6208] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#E6D5B8] rounded-3xl">
          <User className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-semibold text-slate-500">Belum ada karyawan di database.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmployees.map(emp => {
            const isPaid = emp.computedStatus === 'Sudah Gajian';
            const inCart = cart.some(c => c.id === emp.id);

            return (
              <Card key={emp.id} className={`border transition-all overflow-hidden relative group ${isPaid ? 'border-emerald-200 bg-white opacity-80' :
                inCart ? 'border-blue-400 ring-2 ring-blue-100 bg-blue-50/20 shadow-md' : 'border-rose-200 hover:border-rose-400 bg-rose-50/10'
                }`}>
                <CardContent className="p-0">
                  {/* Header */}
                  <div className={`p-4 flex justify-between items-start border-b ${isPaid ? 'bg-emerald-50/50 border-emerald-100' :
                    inCart ? 'bg-blue-100 border-blue-200' : 'bg-rose-50 border-rose-100'
                    }`}>
                    <div className="flex items-center gap-3">
                      {!isPaid && !isBatchMode && (
                        <div
                          className="cursor-pointer shrink-0 mr-1"
                          title={inCart ? "Batal Pilih" : "Pilih Karyawan"}
                          onClick={() => {
                            if (inCart) {
                              setCart(prev => prev.filter(c => c.id !== emp.id));
                            } else {
                              setCart(prev => [...prev, emp]);
                            }
                          }}
                        >
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                            inCart ? 'bg-blue-600 border-blue-600 text-white shadow-xs scale-105' : 'border-slate-300 bg-white hover:border-blue-400'
                          }`}>
                            {inCart && <Check className="w-4 h-4 stroke-[3]" />}
                          </div>
                        </div>
                      )}
                      {isBatchMode ? (
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            if (selectedForDelete.includes(emp.id)) {
                              setSelectedForDelete(selectedForDelete.filter(id => id !== emp.id));
                            } else {
                              setSelectedForDelete([...selectedForDelete, emp.id]);
                            }
                          }}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedForDelete.includes(emp.id) ? 'bg-rose-500 border-rose-500 text-white' : 'border-slate-300'}`}>
                            {selectedForDelete.includes(emp.id) && <CheckCircle2 className="w-4 h-4" />}
                          </div>
                        </div>
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isPaid ? 'bg-emerald-100 text-emerald-600' :
                          inCart ? 'bg-blue-500 text-white' : 'bg-rose-100 text-rose-600'
                          }`}>
                          {isPaid ? <CheckCircle2 className="w-5 h-5" /> : inCart ? <CheckCircle2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-bold text-slate-800 text-sm truncate max-w-[120px]">{emp.full_name}</h3>
                          <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                            (emp.company_category || 'SDE') === 'ASB'
                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                              : 'bg-amber-100 text-amber-800 border border-amber-200'
                          }`}>
                            {(emp.company_category || 'SDE') === 'ASB' ? 'ASB' : 'SDE'}
                          </span>
                        </div>
                        <p className={`text-[10px] font-bold uppercase tracking-widest mt-0.5 ${isPaid ? 'text-emerald-600' :
                          inCart ? 'text-blue-700' : 'text-rose-600'
                          }`}>
                          {inCart ? 'Di Antrean' : emp.computedStatus}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(emp)}
                        className={`p-1.5 rounded-lg transition-colors ${isPaid ? 'text-emerald-600 hover:bg-emerald-100' :
                          inCart ? 'text-blue-600 hover:bg-blue-200' : 'text-slate-600 hover:bg-slate-200'
                          }`}
                        title="Edit Karyawan"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSingle(emp)}
                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-100 hover:text-rose-700 transition-colors"
                        title="Hapus Karyawan"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-4 space-y-4">
                    <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-xs space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Rekening</span>
                        <span className="font-bold text-slate-800">{emp.bank_name}</span>
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                        <span className="text-slate-500 text-[10px]">{emp.account_name}</span>
                        <span className="font-mono font-medium tracking-tight text-slate-700">{emp.bank_account}</span>
                      </div>
                      {emp.email && (
                        <div className="flex items-center justify-between border-t border-slate-200 pt-1.5 text-[10px]">
                          <span className="text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3 text-blue-500" /> Email</span>
                          <span className="text-blue-700 font-medium truncate max-w-[170px]">{emp.email}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Take Home Pay</p>
                          <p className={`font-black text-lg md:text-xl ${inCart ? 'text-blue-700' : 'text-[#8C6208]'}`}>Rp {fmt(emp.computedTHP)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Siklus</p>
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-medium capitalize">
                            {emp.reset_schedule === 'bulanan'
                              ? (emp.monthly_timing === 'awal_bulan' ? 'Bulanan (Awal Bulan)' : 'Bulanan (Akhir Bulan)')
                              : emp.reset_schedule}
                          </span>
                        </div>
                      </div>

                      {/* Breakdown */}
                      <div className="bg-white border border-slate-100 rounded-lg p-3 space-y-1.5 shadow-sm text-[10px] md:text-xs">
                        <div className="flex justify-between text-slate-600">
                          <span>Gaji Pokok</span>
                          <span className="font-medium">Rp {fmt(emp.base_salary)}</span>
                        </div>
                        {(emp.fixed_allowances || []).map((allow, idx) => (
                          <div key={`allow-${idx}`} className="flex justify-between text-emerald-600">
                            <span className="truncate pr-2">{allow.name || 'Penerimaan'}</span>
                            <span className="font-medium shrink-0">+ Rp {fmt(allow.amount)}</span>
                          </div>
                        ))}
                        {(emp.fixed_deductions || []).map((deduc, idx) => (
                          <div key={`deduc-${idx}`} className="flex justify-between text-rose-600">
                            <span className="truncate pr-2">{deduc.name || 'Potongan'}</span>
                            <span className="font-medium shrink-0">- Rp {fmt(deduc.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {isPaid && emp.next_reset_date && (
                      <div className="flex items-center gap-2 text-[10px] text-emerald-700 bg-emerald-50 p-2 rounded-lg font-medium">
                        <CalendarClock className="w-3.5 h-3.5" /> Reset: {new Date(emp.next_reset_date).toLocaleDateString('id-ID')}
                      </div>
                    )}
                  </div>

                  {/* Footer Actions */}
                  {!isBatchMode && (
                    <div className="px-4 pb-4">
                      {isPaid ? (
                        <div className="grid grid-cols-5 gap-2">
                          <Button
                            onClick={() => {
                              setSelectedForPopup(emp);
                              setTimeout(() => {
                                generateSlipPDF();
                              }, 150);
                            }}
                            variant="outline"
                            className="col-span-4 border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-bold h-10 rounded-xl flex items-center justify-center gap-2 shadow-xs transition-all text-xs"
                          >
                            <Download className="w-4 h-4 text-emerald-600" /> Cetak / Unduh Slip
                          </Button>
                          <Button
                            type="button"
                            onClick={async () => {
                              const empEmail = emp.email || teamMembers.find(m => m.full_name?.toLowerCase() === emp.full_name?.toLowerCase())?.email;
                              if (!empEmail) {
                                toast.error(`Email untuk ${emp.full_name} belum diisi. Silakan edit karyawan untuk menambahkan email.`);
                                return;
                              }
                              toast.loading(`Membuat slip & mengirim ke ${empEmail}...`, { id: 'manual-send-email' });
                              const pdfBase64 = await generateSlipPDFBase64(emp);
                              const res = await sendSalarySlipEmail({
                                employee: { ...emp, email: empEmail },
                                paymentPeriod,
                                companySettings,
                                sourceAccount: (emp.company_category || 'SDE') === 'ASB' ? 'Apa Saja Bisa (ASB)' : 'Satala Dermatech Essential (SDE)',
                                pdfBase64
                              });
                              if (res.success) {
                                toast.success(`Slip gaji berhasil dikirim ke ${empEmail}`, { id: 'manual-send-email' });
                              } else if (res.isDomainRestriction) {
                                toast.warning(res.message, { id: 'manual-send-email', duration: 8000 });
                              } else {
                                toast.error(res.message, { id: 'manual-send-email' });
                              }
                            }}
                            variant="outline"
                            title="Kirim Ulang Slip ke Email Karyawan"
                            className="col-span-1 border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 font-bold h-10 rounded-xl flex items-center justify-center shadow-xs transition-all"
                          >
                            <Mail className="w-4 h-4 text-blue-600" />
                          </Button>
                        </div>
                      ) : inCart ? (
                        <Button
                          onClick={() => removeFromCart(emp.id)}
                          variant="outline"
                          className="w-full text-blue-600 border-blue-200 hover:bg-blue-50 font-bold h-10 rounded-xl flex items-center justify-center gap-2"
                        >
                          <X className="w-4 h-4" /> Batal Pilih
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setSelectedForPopup(emp);
                            setSourceAccount((emp.company_category || 'SDE') === 'ASB' ? 'Apa Saja Bisa (ASB)' : 'Satala Dermatech Essential (SDE)');
                          }}
                          className="w-full bg-[#8C6208] hover:bg-[#724F06] text-white font-bold h-10 rounded-xl flex items-center justify-center gap-2"
                        >
                          Pilih untuk Dibayar <ChevronRight className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── POPUP: DETAIL REKENING & TAMBAH KE ANTREAN ─── */}
      {selectedForPopup && (
        <div className="fixed inset-0 bg-black/50  z-40 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden border border-slate-100 transform transition-all scale-100">
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-lg text-slate-800">Detail Transfer</h3>
              <button onClick={() => setSelectedForPopup(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                  <Banknote className="w-8 h-8" />
                </div>
                <p className="text-sm text-slate-500 font-medium">Nominal yang harus ditransfer:</p>
                <p className="text-3xl font-black text-slate-800 mt-1">Rp {fmt(calculateTHP(selectedForPopup))}</p>
                <p className="text-xs font-bold text-blue-600 mt-2 bg-blue-50 py-1 px-3 rounded-full inline-block uppercase tracking-wider">
                  Gaji: {selectedForPopup.full_name}
                </p>
              </div>

              {/* Ad-hoc Components */}
              <div className="space-y-3">
                <div className="flex justify-between items-center group/period">
                  <Label className="font-bold text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    Tambahan / Potongan Khusus
                    <div className="flex items-center text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded ml-1 border border-blue-100">
                      {isEditingPeriod ? (
                        <input
                          autoFocus
                          type="text"
                          value={paymentPeriod}
                          onChange={(e) => setPaymentPeriod(e.target.value)}
                          onBlur={() => setIsEditingPeriod(false)}
                          onKeyDown={(e) => e.key === 'Enter' && setIsEditingPeriod(false)}
                          className="w-24 text-[10px] font-bold uppercase bg-transparent outline-none p-0 h-3 text-center border-b border-blue-300"
                        />
                      ) : (
                        <span className="cursor-pointer font-bold uppercase" onClick={() => setIsEditingPeriod(true)}>({paymentPeriod})</span>
                      )}
                      {!isEditingPeriod && (
                        <Pencil onClick={() => setIsEditingPeriod(true)} className="w-2.5 h-2.5 ml-1 opacity-0 group-hover/period:opacity-100 transition-opacity cursor-pointer text-blue-500" />
                      )}
                    </div>
                  </Label>
                </div>
                {(selectedForPopup.extra_components || []).map((comp, idx) => (
                  <div key={idx} className="flex gap-2 items-start relative group">
                    <Select
                      value={comp.type}
                      onValueChange={v => {
                        const newExt = [...(selectedForPopup.extra_components || [])];
                        newExt[idx].type = v;
                        setSelectedForPopup({ ...selectedForPopup, extra_components: newExt });
                      }}
                    >
                      <SelectTrigger className="h-8 text-[11px] w-24 bg-white border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="allowance">Bonus</SelectItem>
                        <SelectItem value="deduction">Denda</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Keterangan"
                      value={comp.name}
                      onChange={e => {
                        const newExt = [...(selectedForPopup.extra_components || [])];
                        newExt[idx].name = e.target.value;
                        setSelectedForPopup({ ...selectedForPopup, extra_components: newExt });
                      }}
                      className="h-8 text-[11px] bg-white border-slate-200 flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Nominal"
                      value={comp.amount}
                      onChange={e => {
                        const newExt = [...(selectedForPopup.extra_components || [])];
                        newExt[idx].amount = e.target.value;
                        setSelectedForPopup({ ...selectedForPopup, extra_components: newExt });
                      }}
                      className="h-8 text-[11px] bg-white border-slate-200 w-28"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newExt = (selectedForPopup.extra_components || []).filter((_, i) => i !== idx);
                        setSelectedForPopup({ ...selectedForPopup, extra_components: newExt });
                      }}
                      className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newExt = [...(selectedForPopup.extra_components || []), { type: 'allowance', name: '', amount: '' }];
                    setSelectedForPopup({ ...selectedForPopup, extra_components: newExt });
                  }}
                  className="w-full h-8 text-[11px] border-dashed border-slate-300 text-slate-500 hover:text-blue-600 hover:bg-blue-50 gap-1 rounded-lg"
                >
                  <Plus className="w-3 h-3" /> Tambah Bonus / Denda
                </Button>
              </div>

              {/* Sumber Rekening Pengirim */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <Label className="font-bold text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 block">
                  Pilih Rekening Sumber (Pengirim)
                </Label>
                <Select value={sourceAccount} onValueChange={setSourceAccount}>
                  <SelectTrigger className="w-full h-11 bg-white border-slate-200 rounded-xl font-bold text-slate-800 shadow-sm">
                    <SelectValue placeholder="Pilih Rekening Pengirim" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Satala Dermatech Essential (SDE)">
                      <div className="flex items-center gap-2 font-semibold">
                        <Wallet className="w-4 h-4 text-[#8C6208]" />
                        <span>Satala Dermatech Essential (SDE)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Apa Saja Bisa (ASB)">
                      <div className="flex items-center gap-2 font-semibold">
                        <Wallet className="w-4 h-4 text-blue-600" />
                        <span>Apa Saja Bisa (ASB)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-[#F9F5EC] border border-[#E6D5B8] rounded-2xl p-4 space-y-4">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Bank Tujuan</p>
                  <p className="font-black text-slate-800 flex items-center gap-2 text-lg">
                    <Landmark className="w-4 h-4 text-[#8C6208]" /> {selectedForPopup.bank_name}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Nomor Rekening</p>
                  <div className="flex justify-between items-center bg-white border border-[#E6D5B8]/60 rounded-xl px-3 py-2 shadow-sm">
                    <span className="font-mono font-bold text-slate-800 tracking-wider text-base">{selectedForPopup.bank_account}</span>
                    <button
                      onClick={() => copyToClipboard(selectedForPopup.bank_account, 'Nomor Rekening')}
                      className="p-2 bg-slate-50 hover:bg-[#F9F5EC] text-slate-500 hover:text-[#8C6208] rounded-lg transition-colors border border-slate-200"
                      title="Salin Rekening"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Atas Nama</p>
                  <div className="flex justify-between items-center bg-white border border-[#E6D5B8]/60 rounded-xl px-3 py-2 shadow-sm">
                    <span className="font-bold text-slate-800">{selectedForPopup.account_name}</span>
                    <button
                      onClick={() => copyToClipboard(selectedForPopup.account_name, 'Nama Rekening')}
                      className="p-2 bg-slate-50 hover:bg-[#F9F5EC] text-slate-500 hover:text-[#8C6208] rounded-lg transition-colors border border-slate-200"
                      title="Salin Nama"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl flex gap-3 text-xs text-amber-800">
                <ShieldCheck className="w-5 h-5 shrink-0 text-amber-600" />
                <p><strong>Pastikan Anda sudah mentransfer uangnya.</strong> Klik tombol di bawah ini untuk langsung menyelesaikan pembayaran gaji.</p>
              </div>

              <Button onClick={generateSlipPDF} variant="outline" className="w-full h-10 border-blue-200 text-blue-600 hover:bg-blue-50 rounded-xl font-bold flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Cetak Slip (PDF)
              </Button>
            </div>

            <div className="p-4 bg-slate-50 border-t flex gap-2">
              <Button type="button" variant="outline" onClick={() => setSelectedForPopup(null)} className="flex-1 h-12 rounded-xl text-slate-600 font-bold">
                Tutup
              </Button>
              <Button onClick={handleDirectPay} className="flex-[2] h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Gaji Sekarang
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── POPUP: FINALIZE BULK MUTATION ─── */}
      {showFinalizeModal && (
        <div className="fixed inset-0 bg-black/60  z-40 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100">
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-blue-50/50">
              <h3 className="font-bold text-lg text-blue-900">Selesaikan Pembayaran</h3>
              <button onClick={() => setShowFinalizeModal(false)} className="text-blue-400 hover:text-blue-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleFinalizeBulkPay}>
              <div className="p-6 space-y-6 text-center">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner rotate-3">
                  <CreditCard className="w-8 h-8" />
                </div>

                <div>
                  <p className="text-sm text-slate-500 font-medium">Pembayaran gaji untuk <strong>{cart.length} Karyawan</strong> dengan total:</p>
                  <p className="text-4xl font-black text-blue-900 mt-2 tracking-tight">Rp {fmt(cartTotalAmount)}</p>
                </div>

                {/* Mode Selector: Pecah vs Gabung */}
                <div className="text-left bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
                  <Label className="font-bold text-[11px] text-slate-600 uppercase tracking-widest block">
                    Mode Pencatatan Mutasi & Operasional:
                  </Label>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={() => setPayForm({ ...payForm, splitMode: 'split' })}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                        payForm.splitMode === 'split' 
                          ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20 shadow-xs' 
                          : 'bg-white border-slate-200 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <p className="font-bold text-xs text-blue-900 flex items-center gap-1">
                        ⚡ Pecah Per Karyawan
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                        Tercatat <b>{cart.length} baris terpisah</b> (Nama + Gaji bersih per karyawan)
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPayForm({ ...payForm, splitMode: 'combined' })}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                        payForm.splitMode === 'combined' 
                          ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20 shadow-xs' 
                          : 'bg-white border-slate-200 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <p className="font-bold text-xs text-slate-800 flex items-center gap-1">
                        📦 Gabung 1 Transaksi
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                        Tercatat <b>1 baris total</b> (Total {cart.length} Orang)
                      </p>
                    </button>
                  </div>
                </div>

                <div className="text-left bg-slate-50 border border-slate-100 p-5 rounded-2xl space-y-3">
                  <Label className="font-bold text-[11px] text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-blue-500" /> Potong dari Rekening Kas:
                  </Label>
                  <Select value={payForm.bankAccountId} onValueChange={v => setPayForm({ ...payForm, bankAccountId: v })}>
                    <SelectTrigger className="h-12 bg-white font-bold text-slate-800 border-blue-200 shadow-sm text-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bca">{accounts.bca?.name || 'BCA'}</SelectItem>
                      <SelectItem value="mandiri">{accounts.mandiri?.name || 'Mandiri'}</SelectItem>
                      <SelectItem value="bni">{accounts.bni?.name || 'BNI'}</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="space-y-2 border border-slate-200 rounded-xl p-3 bg-white mt-4">
                    <div className="flex justify-between items-center mb-1">
                      <Label className="font-bold text-[11px] uppercase tracking-wider text-slate-500">Alokasi Akun COA</Label>
                      <span className="text-[10px] font-medium text-slate-500">
                        Total: Rp {fmt((payForm.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0))}
                      </span>
                    </div>
                    {(payForm.coa_allocations || []).map((coa, idx) => (
                      <div key={idx} className="flex gap-2 items-start relative group">
                        <div className="flex-1 grid grid-cols-12 gap-2">
                          <div className="col-span-3">
                            <Select
                              value={coa.prefix}
                              onValueChange={val => {
                                const newAlloc = [...payForm.coa_allocations];
                                newAlloc[idx].prefix = val;
                                newAlloc[idx].account_id = '';
                                newAlloc[idx].account_name = '';
                                setPayForm({ ...payForm, coa_allocations: newAlloc });
                              }}
                            >
                              <SelectTrigger className="h-8 text-[11px] bg-slate-50 border-slate-200">
                                <SelectValue placeholder="Kat" />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="1">1 - Aset</SelectItem>
                                <SelectItem value="2">2 - Kwjb</SelectItem>
                                <SelectItem value="3">3 - Ekuitas</SelectItem>
                                <SelectItem value="4">4 - Pdpt</SelectItem>
                                <SelectItem value="5">5 - HPP</SelectItem>
                                <SelectItem value="6">6 - Beban</SelectItem>
                                <SelectItem value="7">7 - P.Lain</SelectItem>
                                <SelectItem value="8">8 - B.Lain</SelectItem>
                                <SelectItem value="9">9 - Pajak</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-5">
                            <Select
                              value={coa.account_id}
                              onValueChange={val => {
                                const selectedCoa = coaList.find(c => c.id === val);
                                const newAlloc = [...payForm.coa_allocations];
                                newAlloc[idx].account_id = val;
                                newAlloc[idx].account_name = selectedCoa ? selectedCoa.name : '';
                                setPayForm({ ...payForm, coa_allocations: newAlloc });
                              }}
                            >
                              <SelectTrigger className="h-8 text-[11px] bg-slate-50 border-slate-200 text-left truncate">
                                <SelectValue placeholder="Pilih Akun" />
                              </SelectTrigger>
                              <SelectContent className="bg-white max-h-48">
                                {coaList.filter(c => !coa.prefix || String(c.id).startsWith(coa.prefix)).map(c => (
                                  <SelectItem key={c.id} value={c.id} className="text-[11px]">
                                    <span className="font-mono text-slate-500 mr-1">{c.id}</span>{c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-4">
                            <Input
                              type="number"
                              placeholder="Nominal"
                              value={coa.amount}
                              onChange={e => {
                                const newAlloc = [...payForm.coa_allocations];
                                newAlloc[idx].amount = e.target.value;
                                setPayForm({ ...payForm, coa_allocations: newAlloc });
                              }}
                              className="h-8 text-[11px] border-slate-200 bg-slate-50"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newAlloc = payForm.coa_allocations.filter((_, i) => i !== idx);
                            setPayForm({ ...payForm, coa_allocations: newAlloc });
                          }}
                          className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg shrink-0 transition-opacity"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setPayForm({ ...payForm, coa_allocations: [...(payForm.coa_allocations || []), { prefix: '', account_id: '', amount: '' }] });
                      }}
                      className="w-full h-8 text-[11px] border-dashed border-slate-300 text-slate-500 hover:text-[#8C6208] hover:border-[#8C6208] hover:bg-[#F9F5EC] gap-1 rounded-lg mt-2"
                    >
                      <Plus className="w-3 h-3" /> Tambah Akun COA
                    </Button>
                  </div>

                  <p className="text-[10px] text-slate-400 leading-relaxed mt-2">Pencatatan ini akan langsung memotong saldo pada rekening Kas yang kamu pilih di atas, dan status {cart.length} karyawan tersebut akan otomatis menjadi <strong>Sudah Gajian</strong>.</p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3">
                <Button type="button" variant="outline" onClick={() => setShowFinalizeModal(false)} className="flex-1 h-12 rounded-xl font-bold">
                  Batal
                </Button>
                <Button type="submit" className="flex-[2] h-12 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-black tracking-wide shadow-md">
                  Ya, Selesaikan & Catat
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Employee Modal (Existing) */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 -xs z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden border border-slate-100">
            <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-800">{editItem ? 'Edit Data Karyawan' : 'Tambah Data Karyawan'}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
            </div>
            <form onSubmit={handleSubmitEmployee}>
              <div className="p-5 space-y-4 text-xs">
                <div className="space-y-1">
                  <Label className="font-bold text-[10px] text-slate-500 uppercase flex items-center justify-between">
                    <span>Nama Lengkap</span>
                    <span className="text-[9px] text-[#8C6208] font-semibold">✨ Bisa pilih dari Tim</span>
                  </Label>
                  <SearchableCombobox
                    value={empForm.full_name}
                    onValueChange={(val) => {
                      const found = employeeNameOptions.find(o => o.value === val);
                      if (found) {
                        setEmpForm(prev => ({
                          ...prev,
                          full_name: found.data.full_name,
                          email: found.data.email || prev.email,
                          account_name: prev.account_name || found.data.account_name,
                          bank_name: found.data.bank_name || prev.bank_name,
                          bank_account: found.data.bank_account || prev.bank_account
                        }));
                      } else {
                        setEmpForm(prev => ({
                          ...prev,
                          full_name: val
                        }));
                      }
                    }}
                    options={employeeNameOptions}
                    placeholder="Ketik nama atau pilih dari database tim..."
                    searchPlaceholder="Cari nama karyawan / tim..."
                    allowCustom={true}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="font-bold text-[10px] text-slate-500 uppercase flex items-center justify-between">
                    <span>Email Karyawan (Kirim Slip Gaji)</span>
                    <span className="text-[9px] text-slate-400 font-normal lowercase">contoh: karyawan@gmail.com</span>
                  </Label>
                  <Input
                    type="email"
                    value={empForm.email || ''}
                    onChange={e => setEmpForm({ ...empForm, email: e.target.value })}
                    className="h-9 font-medium"
                    placeholder="karyawan@gmail.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="font-bold text-[10px] text-slate-500 uppercase">Nama Bank</Label>
                    <Input required value={empForm.bank_name} onChange={e => setEmpForm({ ...empForm, bank_name: e.target.value })} className="h-9" placeholder="BCA / Mandiri" />
                  </div>
                  <div className="space-y-1">
                    <Label className="font-bold text-[10px] text-slate-500 uppercase">No Rekening</Label>
                    <Input required value={empForm.bank_account} onChange={e => setEmpForm({ ...empForm, bank_account: e.target.value })} className="h-9 font-mono" placeholder="12345678" />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="font-bold text-[10px] text-slate-500 uppercase">Kategori Perusahaan (Sumber Gaji)</Label>
                  <Select value={empForm.company_category || 'SDE'} onValueChange={v => setEmpForm({ ...empForm, company_category: v })}>
                    <SelectTrigger className="h-9 bg-white font-semibold">
                      <SelectValue placeholder="Pilih Kategori Perusahaan" />
                    </SelectTrigger>
                    <SelectContent className="z-50">
                      <SelectItem value="SDE">Satala Dermatech Essential (SDE)</SelectItem>
                      <SelectItem value="ASB">Apa Saja Bisa (ASB)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="font-bold text-[10px] text-slate-500 uppercase">Atas Nama Rekening</Label>
                  <Input required value={empForm.account_name} onChange={e => setEmpForm({ ...empForm, account_name: e.target.value })} className="h-9" placeholder="JOHN DOE" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="font-bold text-[10px] text-slate-500 uppercase">Gaji Pokok (Rp)</Label>
                    <Input required type="number" value={empForm.base_salary} onChange={e => setEmpForm({ ...empForm, base_salary: e.target.value })} className="h-9 font-bold" placeholder="5000000" />
                  </div>
                  <div className="space-y-1">
                    <Label className="font-bold text-[10px] text-slate-500 uppercase">Siklus Pembayaran</Label>
                    <Select value={empForm.reset_schedule} onValueChange={v => setEmpForm({ ...empForm, reset_schedule: v })}>
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-50">
                        <SelectItem value="harian">Harian</SelectItem>
                        <SelectItem value="mingguan">Mingguan</SelectItem>
                        <SelectItem value="bulanan">Bulanan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {empForm.reset_schedule === 'bulanan' && (
                  <div className="space-y-1 bg-amber-50/70 border border-amber-200/80 rounded-xl p-3 animate-in fade-in">
                    <Label className="font-bold text-[10px] text-[#8C6208] uppercase flex items-center justify-between">
                      <span>Waktu Reset / Gajian Bulanan</span>
                      <span className="text-[9px] text-slate-500 font-normal">Pilih waktu reset siklus</span>
                    </Label>
                    <Select
                      value={empForm.monthly_timing || 'akhir_bulan'}
                      onValueChange={v => setEmpForm({ ...empForm, monthly_timing: v })}
                    >
                      <SelectTrigger className="h-9 bg-white font-bold border-amber-300">
                        <SelectValue placeholder="Pilih Waktu Gajian" />
                      </SelectTrigger>
                      <SelectContent className="z-50">
                        <SelectItem value="akhir_bulan" className="font-bold text-[#8C6208]">
                          ✨ Akhir Bulan (Tgl 28/29/30/31 otomatis)
                        </SelectItem>
                        <SelectItem value="awal_bulan" className="font-bold text-blue-700">
                          🗓️ Awal Bulan (Tgl 1 setiap bulan)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-3 mt-4">
                  {empForm.salary_components.map((comp, idx) => (
                    <div key={idx} className="flex gap-2 items-end relative group mt-2">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="font-bold text-[9px] text-slate-400 uppercase">Jenis Kategori</Label>
                          <Select
                            value={comp.type}
                            onValueChange={v => {
                              const newComps = [...empForm.salary_components];
                              newComps[idx].type = v;
                              // clear the name to avoid mismatch if they switch from allowance to deduction
                              newComps[idx].name = '';
                              setEmpForm({ ...empForm, salary_components: newComps });
                            }}
                          >
                            <SelectTrigger className="h-9 bg-white focus:ring-slate-400 font-bold border-slate-200">
                              <SelectValue placeholder="Pilih Jenis" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="allowance" className="text-emerald-700 font-bold">Penerimaan (+)</SelectItem>
                              <SelectItem value="deduction" className="text-rose-700 font-bold">Potongan (-)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="font-bold text-[9px] text-slate-400 uppercase">
                            Kategori {comp.type === 'allowance' ? 'Penerimaan' : 'Potongan'}
                          </Label>
                          <SearchableCombobox
                            value={comp.name}
                            onValueChange={v => {
                              const newComps = [...empForm.salary_components];
                              newComps[idx].name = v;
                              setEmpForm({ ...empForm, salary_components: newComps });
                            }}
                            options={comp.type === 'allowance' ? ALLOWANCE_OPTIONS : DEDUCTION_OPTIONS}
                            placeholder="Pilih atau ketik..."
                            searchPlaceholder="Cari kategori..."
                            allowCustom={true}
                          />
                        </div>
                        <div className="col-span-2 space-y-1">
                          <Label className="font-bold text-[9px] text-slate-400 uppercase">Nominal (Rp)</Label>
                          <Input
                            type="number"
                            placeholder="Contoh: 1500000"
                            value={comp.amount}
                            onChange={e => {
                              const newComps = [...empForm.salary_components];
                              newComps[idx].amount = e.target.value;
                              setEmpForm({ ...empForm, salary_components: newComps });
                            }}
                            className="h-9 font-bold bg-white focus-visible:ring-slate-400"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newComps = empForm.salary_components.filter((_, i) => i !== idx);
                          setEmpForm({ ...empForm, salary_components: newComps });
                        }}
                        className="w-9 h-9 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-xl shrink-0 transition-colors mb-[2px]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEmpForm({
                        ...empForm,
                        salary_components: [...empForm.salary_components, { type: 'allowance', name: '', amount: '' }]
                      });
                    }}
                    className="w-full h-10 border-dashed border-[#8C6208] text-[#8C6208] font-bold hover:bg-[#F9F5EC] hover:text-[#8C6208] gap-1 rounded-xl mt-2"
                  >
                    <Plus className="w-4 h-4" /> Tambah Kategori
                  </Button>
                </div>
              </div>
              <div className="p-4 bg-slate-50 border-t flex justify-between items-center gap-2">
                {editItem ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleDeleteSingle(editItem)}
                    className="h-9 px-3 text-rose-600 hover:bg-rose-100 hover:text-rose-700 font-bold text-xs flex items-center gap-1.5 rounded-xl"
                  >
                    <Trash2 className="w-4 h-4" /> Hapus Karyawan
                  </Button>
                ) : <div />}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="h-9 rounded-xl">Batal</Button>
                  <Button type="submit" className="h-9 rounded-xl bg-[#8C6208] text-white hover:bg-[#724F06] font-bold">Simpan</Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HIDDEN TEMPLATE FOR PDF GENERATION */}
      {selectedForPopup && (
        <div className="absolute -left-[9999px] top-0 pointer-events-none bg-white">
          <div id="pdf-salary-slip-template" className="w-[800px] min-h-[1131px] bg-[#faf8f5] text-[#302621] p-7 relative flex flex-col justify-start border border-slate-100 rounded-lg font-sans" style={{ fontFamily: "'Inter', Arial, Helvetica, sans-serif" }}>
            <div>
              {/* Header */}
              <div className="flex justify-between items-start mb-2.5">
                {/* Left: Logo & Company Address */}
                <div className="flex items-center gap-3">
                  <img 
                    src={companySettings?.logo_url || "/logo.png"} 
                    alt="Logo Satala" 
                    className="h-11 w-auto object-contain shrink-0" 
                    crossOrigin="anonymous" 
                  />
                  <div>
                    <h1 className="text-base font-bold text-[#302621] uppercase tracking-wide">
                      {companySettings?.nama_perusahaan || "PT. Satala Dermatech Essential"}
                    </h1>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-tight max-w-[340px]">
                      {companySettings?.alamat_perusahaan || "Jalan Aster Kavling Paspampres, Kota Batu, Kec. Ciomas, Bogor"}
                    </p>
                  </div>
                </div>

                {/* Right: Title & Period */}
                <div className="text-right">
                  <h2 className="text-sm font-bold tracking-wider text-[#302621] uppercase">SLIP GAJI KARYAWAN</h2>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Periode: <span className="text-[#302621] font-semibold">{paymentPeriod}</span>
                  </div>
                </div>
              </div>
              
              {/* Gold Divider */}
              <div className="w-full h-[2px] bg-[#d4b986] mb-3"></div>

              {/* TOP CARDS */}
              <div className="grid grid-cols-2 gap-3.5 mb-3.5">
                
                {/* Informasi Karyawan */}
                <div className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#4b3b2e] flex items-center justify-center text-white shrink-0 shadow-sm">
                      <User className="w-4 h-4" />
                    </div>
                    <h4 className="font-bold text-[#4b3b2e] text-[12px] tracking-wide h-8 flex items-center">INFORMASI KARYAWAN</h4>
                  </div>
                  
                  <div className="space-y-2 text-[11px] text-slate-600">
                    <div className="flex"><span className="w-28 text-slate-500">NIK</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{teamMembers.find(m => m.full_name?.toLowerCase() === selectedForPopup.full_name?.toLowerCase())?.nik || selectedForPopup.nik || '-'}</span></div>
                    <div className="flex"><span className="w-28 text-slate-500">Nama</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{selectedForPopup.full_name}</span></div>
                    <div className="flex"><span className="w-28 text-slate-500">Jabatan</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{teamMembers.find(m => m.full_name?.toLowerCase() === selectedForPopup.full_name?.toLowerCase())?.role || '-'}</span></div>
                    <div className="flex"><span className="w-28 text-slate-500">Email</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{teamMembers.find(m => m.full_name?.toLowerCase() === selectedForPopup.full_name?.toLowerCase())?.email || '-'}</span></div>
                  </div>
                </div>

                {/* Informasi Payroll */}
                <div className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#4b3b2e] flex items-center justify-center text-white shrink-0 shadow-sm">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <h4 className="font-bold text-[#4b3b2e] text-[12px] tracking-wide h-8 flex items-center">INFORMASI PAYROLL</h4>
                  </div>
                  
                  <div className="space-y-2 text-[11px] text-slate-600">
                    <div className="flex"><span className="w-32 text-slate-500">Tanggal Payroll</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{new Date().getDate()} {paymentPeriod}</span></div>
                    <div className="flex"><span className="w-32 text-slate-500">Metode Bayar</span><span className="w-3">:</span><span className="font-semibold text-slate-800">Transfer {selectedForPopup.bank_name}</span></div>
                    <div className="flex"><span className="w-32 text-slate-500">Bank</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{selectedForPopup.bank_name} - {selectedForPopup.bank_account}</span></div>
                    <div className="flex"><span className="w-32 text-slate-500">Rekening Atas Nama</span><span className="w-3">:</span><span className="font-semibold text-slate-800">{selectedForPopup.account_name}</span></div>
                  </div>
                </div>
              </div>

              {/* Rincian Komponen Gaji */}
              <div className="bg-[#fdfbf7] rounded-2xl border border-slate-200/70 p-4 shadow-sm mb-3.5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[#faf4ea] border border-[#f0e3d0] flex items-center justify-center text-[#4b3b2e] shrink-0">
                    <Coins className="w-4 h-4 text-[#4b3b2e]" />
                  </div>
                  <h4 className="font-bold text-[#4b3b2e] text-[12px] tracking-wide h-8 flex items-center">RINCIAN KOMPONEN GAJI</h4>
                </div>

                <div className="rounded-xl overflow-hidden border border-slate-200/80 bg-white">
                  <table className="w-full text-[11px]" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-[#4b3b2e] text-white">
                        <th className="py-2.5 px-4 text-left font-bold w-1/2 border-r border-[#634e3e]">DESKRIPSI KOMPONEN</th>
                        <th className="py-2.5 px-4 text-right font-bold w-1/4 border-r border-[#634e3e]">PENERIMAAN (IDR)</th>
                        <th className="py-2.5 px-4 text-right font-bold w-1/4">POTONGAN (IDR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white border-b border-slate-100">
                        <td className="py-2.5 px-4 text-slate-800 font-semibold border-r border-slate-100">Gaji Pokok</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-slate-800 border-r border-slate-100">{fmt(selectedForPopup.base_salary)}</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-slate-400">-</td>
                      </tr>
                      {(selectedForPopup.fixed_allowances || []).map((a, i) => (
                        <tr key={`fa-${i}`} className="bg-white border-b border-slate-100">
                          <td className="py-2.5 px-4 text-slate-800 font-medium border-r border-slate-100">{a.name}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-800 border-r border-slate-100">{fmt(a.amount)}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-400">-</td>
                        </tr>
                      ))}
                      {(selectedForPopup.extra_components || []).filter(c => c.type === 'allowance').map((a, i) => (
                        <tr key={`ea-${i}`} className="bg-white border-b border-slate-100">
                          <td className="py-2.5 px-4 text-slate-800 font-medium border-r border-slate-100">{a.name}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-800 border-r border-slate-100">{fmt(a.amount)}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-400">-</td>
                        </tr>
                      ))}
                      {(selectedForPopup.fixed_deductions || []).map((d, i) => (
                        <tr key={`fd-${i}`} className="bg-white border-b border-slate-100">
                          <td className="py-2.5 px-4 text-slate-800 font-medium border-r border-slate-100">{d.name}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-400 border-r border-slate-100">-</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-800">{fmt(d.amount)}</td>
                        </tr>
                      ))}
                      {(selectedForPopup.extra_components || []).filter(c => c.type === 'deduction').map((d, i) => (
                        <tr key={`ed-${i}`} className="bg-white border-b border-slate-100">
                          <td className="py-2.5 px-4 text-slate-800 font-medium border-r border-slate-100">{d.name}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-400 border-r border-slate-100">-</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-800">{fmt(d.amount)}</td>
                        </tr>
                      ))}
                      {(!selectedForPopup.fixed_deductions || selectedForPopup.fixed_deductions.length === 0) && (!selectedForPopup.extra_components || selectedForPopup.extra_components.filter(c => c.type === 'deduction').length === 0) && (
                        <tr className="bg-white border-b border-slate-100">
                          <td className="py-2.5 px-4 text-slate-800 font-medium border-r border-slate-100">Pajak Penghasilan (PPh 21)</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-400 border-r border-slate-100">-</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-slate-800">0,00</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#faf4ea] border-t-2 border-[#d4b986]">
                        <td className="py-2.5 px-4 font-bold text-[#4b3b2e] uppercase text-[11px] border-r border-[#e0cfb3]">TOTAL</td>
                        <td className="py-2.5 px-4 text-right font-bold text-[#4b3b2e] text-[11px] border-r border-[#e0cfb3]">
                          Rp {fmt(Number(selectedForPopup.base_salary) +
                            (selectedForPopup.fixed_allowances || []).reduce((s, a) => s + Number(a.amount), 0) +
                            (selectedForPopup.extra_components || []).filter(c => c.type === 'allowance').reduce((s, a) => s + Number(a.amount), 0))
                          }
                        </td>
                        <td className="py-2.5 px-4 text-right font-bold text-[#4b3b2e] text-[11px]">
                          Rp {fmt((selectedForPopup.fixed_deductions || []).reduce((s, a) => s + Number(a.amount), 0) +
                            (selectedForPopup.extra_components || []).filter(c => c.type === 'deduction').reduce((s, a) => s + Number(a.amount), 0))
                          }
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* BOTTOM 2 CARDS: TAKE HOME PAY & RINGKASAN PAYROLL */}
              <div className="grid grid-cols-2 gap-3.5 mb-5">
                {/* Left Card: Take Home Pay */}
                <div className="bg-[#fdfbf7] rounded-2xl border border-slate-200/70 p-4 shadow-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-[#4b3b2e] flex items-center justify-center text-white shrink-0 shadow-sm">
                        <Wallet className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#4b3b2e] text-[11px] uppercase tracking-wide leading-none">TAKE HOME PAY</h4>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">(GAJI BERSIH)</p>
                      </div>
                    </div>
                    <div className="text-[25px] font-bold text-[#4b3b2e] font-serif tracking-tight mt-1 mb-1">
                      Rp {fmt(calculateTHP(selectedForPopup))}
                    </div>
                    <p className="text-[10px] text-slate-500 italic">Gaji bersih telah ditransfer ke rekening Anda</p>
                  </div>


                </div>

                {/* Right Card: Ringkasan Payroll */}
                <div className="bg-[#fdfbf7] rounded-2xl border border-slate-200/70 p-4 shadow-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#faf4ea] border border-[#f0e3d0] flex items-center justify-center text-[#4b3b2e] shrink-0">
                        <PieChart className="w-4 h-4 text-[#4b3b2e]" />
                      </div>
                      <h4 className="font-bold text-[#4b3b2e] text-[11px] uppercase tracking-wide h-8 flex items-center">RINGKASAN PAYROLL</h4>
                    </div>

                    <div className="space-y-2 text-[11px]">
                      <div className="flex justify-between text-slate-600">
                        <span>Total Penerimaan</span>
                        <span className="font-medium text-slate-800">
                          Rp {fmt(Number(selectedForPopup.base_salary) +
                            (selectedForPopup.fixed_allowances || []).reduce((s, a) => s + Number(a.amount), 0) +
                            (selectedForPopup.extra_components || []).filter(c => c.type === 'allowance').reduce((s, a) => s + Number(a.amount), 0))
                          }
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Total Potongan</span>
                        <span className="font-medium text-slate-800">
                          Rp {fmt((selectedForPopup.fixed_deductions || []).reduce((s, a) => s + Number(a.amount), 0) +
                            (selectedForPopup.extra_components || []).filter(c => c.type === 'deduction').reduce((s, a) => s + Number(a.amount), 0))
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-[#d4b986]">
                    <div className="flex justify-between items-center text-[11px]">
                      <span className="font-bold text-[#4b3b2e]">Total Gaji Bersih</span>
                      <span className="font-bold text-[#8C6208] text-[13px]">Rp {fmt(calculateTHP(selectedForPopup))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Signatures */}
            {(() => {
              const penerimaMember = teamMembers.find(m => m.full_name?.toLowerCase() === selectedForPopup.full_name?.toLowerCase());
              const pengirimMember = teamMembers.find(m => m.signature_url && (m.role?.toLowerCase().includes('finance') || m.full_name?.toLowerCase().includes('anissa'))) 
                || teamMembers.find(m => m.role?.toLowerCase().includes('finance')) 
                || teamMembers.find(m => m.full_name?.toLowerCase().includes('anissa'));

              return (
                <div className="flex justify-between items-end px-8 mt-8 mb-2 text-xs text-slate-700 text-center">
                  {/* Left: Penerima */}
                  <div className="flex flex-col items-center">
                    <p className="mb-1">Penerima,</p>
                    {penerimaMember?.signature_url ? (
                      <img 
                        src={penerimaMember.signature_url} 
                        alt="TTD Penerima" 
                        className="h-20 max-w-[140px] w-auto object-contain my-1" 
                        {...(penerimaMember.signature_url.startsWith('http') ? { crossOrigin: 'anonymous' } : {})}
                      />
                    ) : (
                      <div className="h-20"></div>
                    )}
                    <p className="font-bold text-[#302621] underline mt-1">{selectedForPopup.full_name}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {penerimaMember?.role || 'Karyawan'}
                    </p>
                  </div>

                  {/* Right: Pengirim */}
                  <div className="flex flex-col items-center">
                    <p className="text-slate-500 mb-0.5">Bogor, {new Date().getDate()} {paymentPeriod}</p>
                    <p className="mb-1">Pengirim,</p>
                    {pengirimMember?.signature_url ? (
                      <img 
                        src={pengirimMember.signature_url} 
                        alt="TTD Pengirim" 
                        className="h-20 max-w-[140px] w-auto object-contain my-1" 
                        {...(pengirimMember.signature_url.startsWith('http') ? { crossOrigin: 'anonymous' } : {})}
                      />
                    ) : (
                      <div className="h-20"></div>
                    )}
                    <p className="font-bold text-[#302621] underline mt-1">
                      {pengirimMember?.full_name || 'Anissa Nurul Fadilah'}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {pengirimMember?.role || 'Finance Manager'}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Footer Disclaimer */}
            <div className="mt-8 pt-3 border-t border-slate-200 text-center">
              <p className="text-[9px] text-slate-400 italic">
                * Dokumen ini diterbitkan secara otomatis oleh sistem payroll dan sah tanpa stempel fisik jika diverifikasi secara internal.
              </p>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
